import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { lazy, Suspense, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isNotAvailable } from '@/api/client'
import { PageHeader } from '@/components/page'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  appointmentsQuery,
  deadlineKeys,
  deadlinesRangeQuery,
  rotateSubscription,
  subscriptionQuery,
} from '@/features/deadlines/api'
import {
  APPOINTMENT_COLOR,
  DUE_COLOR,
  PERMIT_COLOR,
  toCalendarEvents,
} from '@/features/deadlines/calendarEvents'
import { AMPEL_COLOR } from '@/features/orders/ampel'
import { requireNav } from '@/nav/guard'

// FullCalendar (mit Plugins und Sprachen) erst laden, wenn der Kalender geöffnet wird
const CalendarView = lazy(() => import('@/features/deadlines/CalendarView'))

export const Route = createFileRoute('/_app/calendar')({
  beforeLoad: requireNav('calendar'),
  component: CalendarPage,
})

interface Range {
  from: string
  to: string
}

function CalendarPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [range, setRange] = useState<Range | null>(null)

  const deadlines = useQuery({
    ...deadlinesRangeQuery(range?.from ?? '', range?.to ?? ''),
    enabled: range !== null,
  })
  const appointments = useQuery({
    ...appointmentsQuery(range?.from ?? '', range?.to ?? ''),
    enabled: range !== null,
  })
  const events = useMemo(
    () => toCalendarEvents(deadlines.data ?? [], appointments.data ?? [], t),
    [deadlines.data, appointments.data, t],
  )

  const legend = [
    { color: AMPEL_COLOR.red, label: `${t('calendar.actionDue')}: ${t('ampel.red')}` },
    { color: AMPEL_COLOR.amber, label: `${t('calendar.actionDue')}: ${t('ampel.amber')}` },
    { color: AMPEL_COLOR.green, label: `${t('calendar.actionDue')}: ${t('ampel.green')}` },
    { color: DUE_COLOR, label: t('calendar.due') },
    { color: PERMIT_COLOR, label: t('calendar.permitExpiry') },
    { color: APPOINTMENT_COLOR, label: t('calendar.appointment') },
  ]

  const sources = [
    { name: t('pages.deadlines.title'), query: deadlines },
    { name: t('calendar.appointment'), query: appointments },
  ]

  return (
    <>
      <PageHeader title={t('pages.calendar.title')} description={t('pages.calendar.description')} />

      {sources.map(({ name, query }) =>
        query.isError ? (
          <p
            key={name}
            role="status"
            className="mb-3 rounded-md bg-warning p-3 text-sm text-warning-foreground"
          >
            {name}:{' '}
            {isNotAvailable(query.error) ? t('errors.notConnectedBody') : t('calendar.loadError')}
          </p>
        ) : null,
      )}

      <div
        className="rounded-lg border bg-card p-3"
        data-testid="calendar"
        role="region"
        aria-label={t('calendar.calendarLabel')}
        aria-busy={deadlines.isFetching || appointments.isFetching}
      >
        <Suspense fallback={<Skeleton className="h-96 w-full" aria-label={t('common.loading')} />}>
          <CalendarView
            events={events}
            onRange={(next) =>
              setRange((prev) => (prev?.from === next.from && prev.to === next.to ? prev : next))
            }
            onOpenOrder={(orderId) =>
              void navigate({ to: '/orders/$orderId', params: { orderId } })
            }
          />
        </Suspense>
      </div>

      <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2" aria-label={t('calendar.legend')}>
        {legend.map((entry) => (
          <li key={entry.label} className="flex items-center gap-2 text-sm">
            <span
              aria-hidden="true"
              className="inline-block size-3 rounded-sm"
              style={{ backgroundColor: entry.color }}
            />
            {entry.label}
          </li>
        ))}
      </ul>

      <SubscriptionCard />
    </>
  )
}

/** iCal-Abo (A-26): Link nur beim Erzeugen sichtbar; ohne Backend-Endpunkt ein Hinweis. */
function SubscriptionCard() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const subscription = useQuery(subscriptionQuery)
  const rotate = useMutation({
    mutationFn: rotateSubscription,
    onSuccess: (data) => queryClient.setQueryData(deadlineKeys.subscription, data),
  })
  const current = rotate.data ?? subscription.data
  const unavailable = subscription.isError && isNotAvailable(subscription.error)

  return (
    <section className="mt-6 rounded-lg border bg-card p-4 text-sm" aria-labelledby="ical-title">
      <h2 id="ical-title" className="mb-2 font-medium">
        {t('calendar.icalTitle')}
      </h2>
      {unavailable ? (
        <p className="text-muted-foreground">{t('calendar.icalNote')}</p>
      ) : subscription.isError ? (
        <p role="alert" className="text-destructive">
          {t('calendar.icalError')}
        </p>
      ) : (
        <div className="grid gap-3">
          {current?.feedUrl ? (
            <div className="grid gap-1.5">
              <Label htmlFor="ical-url">{t('calendar.icalLink')}</Label>
              <Input
                id="ical-url"
                readOnly
                value={current.feedUrl}
                onFocus={(e) => e.target.select()}
              />
            </div>
          ) : current?.exists ? (
            <p className="text-muted-foreground">{t('calendar.icalExists')}</p>
          ) : null}
          <div>
            <Button
              type="button"
              variant="outline"
              disabled={rotate.isPending || subscription.isPending}
              onClick={() => rotate.mutate()}
            >
              {current?.exists ? t('calendar.icalRotate') : t('calendar.icalCreate')}
            </Button>
          </div>
          {rotate.isError && (
            <p role="alert" className="text-destructive">
              {t('calendar.icalError')}
            </p>
          )}
        </div>
      )}
    </section>
  )
}
